import { SafeAreaView, ScrollView, SxProp, View } from 'dripsy';
import React from 'react';

import { PageContainer } from '@/features/earn/components/page/PageContainer';
import { PageItem } from '@/features/earn/components/page/PageItem';
import { DevToolsPageHeader } from '../components/DevToolsPageHeader';
import { DevToolsStackRoutes, DevToolsStackScreenProps } from '../types';
import { H6, TextBody, TextTitle } from '@/design-system/components/typography';
import { SegmentSelect } from '@/design-system/components/buttons/segmentSelect/SegmentSelect';
import { palette } from '@/brand-utgl/theme';
import { theme } from '@/brand-utgl/theme';
import { keyGenerator } from '@/utils/keyGenerator';

export const DevToolsColours: React.FC<DevToolsStackScreenProps<DevToolsStackRoutes.Colours>> = (props) => {
  const [tabIndex, setTabIndex] = React.useState(0);
  const TABS = [
    {
      name: 'Palette'
    },
    {
      name: 'Theming'
    }
  ];

  const checkColourContrast = (hexcolor: string) => {
    // If a leading # is provided, remove it
    if (hexcolor.slice(0, 1) === '#') {
      hexcolor = hexcolor.slice(1);
    }

    // If a three-character hexcode, make six-character
    if (hexcolor.length === 3) {
      hexcolor = hexcolor.split('').join('');
    }

    // Convert to RGB value
    const r = parseInt(hexcolor.substr(0, 2), 16);
    const g = parseInt(hexcolor.substr(2, 2), 16);
    const b = parseInt(hexcolor.substr(4, 2), 16);

    // Get YIQ ratio
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;

    // Check contrast
    return yiq >= 128 ? 'black' : 'white';
  };

  return (
    <SafeAreaView sx={styles.container}>
      <ScrollView showsVerticalScrollIndicator={false}>
        <PageContainer sx={{ paddingX: '$10', paddingY: '$10', backgroundColor: 'transparent' }}>
          <DevToolsPageHeader currentPage={DevToolsStackRoutes.Colours} />
          {/* <Divider /> */}
          <PageItem sx={{ ...styles.pageItem, gap: '$7' }}>
            <View sx={{ width: 'fit-content' }}>
              <SegmentSelect
                data={TABS.map((tab, i) => ({ labelKey: tab.name, value: i }))}
                value={tabIndex}
                onValueChange={setTabIndex}
                buttonWidth={150}
              />
            </View>
            <View>
              {tabIndex === 0 && (
                <View sx={{ gap: '5' }}>
                  <View sx={{ gap: '$5' }}>
                    {Object.keys(palette).map((key) => (
                      <View key={keyGenerator('colors')} sx={{ flexDirection: 'row', flexWrap: 'wrap', gap: '$2' }}>
                        {Object.keys(palette[key]).map((key2) => (
                          <PageItem
                            key={keyGenerator('colors')}
                            sx={{ ...styles.pageItem, width: 180, height: 250, backgroundColor: palette[key][key2] }}>
                            <TextTitle
                              semiBold
                              sx={{
                                // colour contrast
                                color: checkColourContrast(palette[key][key2])
                              }}>
                              {key} {key2}
                            </TextTitle>
                          </PageItem>
                        ))}
                      </View>
                    ))}
                  </View>
                </View>
              )}
              {tabIndex === 1 && (
                <View sx={{ gap: '5' }}>
                  <View sx={{ flexDirection: 'row', flexWrap: 'wrap', gap: '$2' }}>
                    {Object.keys(theme.light.colors).map((key) => (
                      <PageItem
                        key={keyGenerator('colors')}
                        sx={{ ...styles.pageItem, flexGrow: 1, width: 330, height: 100, backgroundColor: theme.light.colors[key] }}>
                        <TextBody
                          semiBold
                          sx={{
                            // colour contrast
                            color: checkColourContrast(theme.light.colors[key])
                          }}>
                          {key}
                        </TextBody>
                        <TextBody
                          semiBold
                          sx={{
                            // colour contrast
                            color: checkColourContrast(theme.light.colors[key])
                          }}>
                          {theme.light.colors[key]}
                        </TextBody>
                      </PageItem>
                    ))}
                  </View>
                </View>
              )}
            </View>
          </PageItem>
        </PageContainer>
      </ScrollView>
    </SafeAreaView>
  );
};

enum StyleName {
  container = 'container',
  pageItem = 'pageItem'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    height: '100%',
    width: '100%',
    backgroundColor: '$backgroundPrimary'
  },
  pageItem: {
    width: '100%',
    padding: '$7',
    borderRadius: 10,
    gap: '$2'
  }
};
